'use client';

import { useState } from 'react';
import { useAudioRecorder } from '@/lib/useAudioRecorder';
import { Recorder } from '@/components/Recorder';

type InputMode = 'text' | 'voice';

export default function Home() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [text, setText] = useState('');
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [usedVoice, setUsedVoice] = useState(false);

  const {
    isRecording,
    isTranscribing,
    transcript,
    error: recorderError,
    isSupported,
    startRecording,
    stopRecording,
    reset,
  } = useAudioRecorder();

  // 音声認識結果をテキストに反映
  const handleStopRecording = async () => {
    const result = await stopRecording();
    if (result) {
      setText(prev => prev ? prev + '\n' + result : result);
      setUsedVoice(true);
    }
  };

  async function handleWrite() {
    if (!text.trim()) {
      setError('作業内容を入力してください');
      return;
    }

    setWriting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch('/api/write-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          text: text.trim(),
          inputMode: usedVoice ? 'voice' : 'text'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '書き込みに失敗しました');
      }

      setSuccessMessage('記録しました');
      setText('');
      setUsedVoice(false);
      reset();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('書き込みに失敗しました');
      }
    } finally {
      setWriting(false);
    }
  }

  return (
    <main style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)',
      padding: '40px 16px'
    }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#fff',
            margin: '0 0 8px 0'
          }}>
            NikkaLog
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
            自分記録ツール
          </p>
        </div>

        {/* Error Message */}
        {(error || recorderError) && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#fca5a5', fontSize: '14px' }}>{error || recorderError}</span>
            <button
              onClick={() => { setError(null); reset(); }}
              style={{
                background: 'none',
                border: 'none',
                color: '#f87171',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0 4px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#86efac', fontSize: '14px' }}>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#4ade80',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0 4px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Main Card */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(71, 85, 105, 0.5)',
          borderRadius: '20px',
          padding: '28px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}>
          {/* Mode Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '24px'
          }}>
            <button
              onClick={() => setInputMode('text')}
              style={{
                padding: '10px 24px',
                borderRadius: '9999px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: inputMode === 'text'
                  ? 'linear-gradient(135deg, #10b981, #06b6d4)'
                  : 'rgba(255,255,255,0.1)',
                color: 'white'
              }}
            >
              ✏️ テキスト
            </button>
            <button
              onClick={() => setInputMode('voice')}
              style={{
                padding: '10px 24px',
                borderRadius: '9999px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: inputMode === 'voice'
                  ? 'linear-gradient(135deg, #10b981, #06b6d4)'
                  : 'rgba(255,255,255,0.1)',
                color: 'white'
              }}
            >
              🎤 音声
            </button>
          </div>

          {/* Date Input */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#cbd5e1',
              marginBottom: '8px'
            }}>
              日付
            </label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{
                width: '100%',
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '12px',
                padding: '14px 16px',
                color: '#fff',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Voice Mode */}
          {inputMode === 'voice' && (
            <div style={{ marginBottom: '24px' }}>
              <Recorder
                isRecording={isRecording}
                isTranscribing={isTranscribing}
                error={null}
                isSupported={isSupported}
                onStart={startRecording}
                onStop={handleStopRecording}
              />
            </div>
          )}

          {/* Text Input */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '500',
              color: '#cbd5e1',
              marginBottom: '8px'
            }}>
              {inputMode === 'voice' ? '認識結果（編集可能）' : '記録内容'}
              {usedVoice && (
                <span style={{
                  marginLeft: '8px',
                  padding: '2px 8px',
                  borderRadius: '9999px',
                  background: 'rgba(147, 51, 234, 0.2)',
                  color: '#c4b5fd',
                  fontSize: '11px'
                }}>
                  🎤 音声入力済み
                </span>
              )}
            </label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={inputMode === 'voice'
                ? '音声認識の結果がここに表示されます...'
                : '今日の記録を入力...'}
              style={{
                width: '100%',
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                borderRadius: '12px',
                padding: '14px 16px',
                color: '#fff',
                fontSize: '14px',
                fontFamily: 'monospace',
                lineHeight: '1.6',
                resize: 'vertical',
                minHeight: inputMode === 'voice' ? '120px' : '200px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            <div style={{
              textAlign: 'right',
              marginTop: '8px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              {text.length} 文字
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleWrite}
            disabled={writing || !text.trim() || isRecording || isTranscribing}
            style={{
              width: '100%',
              padding: '16px 24px',
              borderRadius: '12px',
              border: 'none',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (writing || !text.trim() || isRecording || isTranscribing) ? 'not-allowed' : 'pointer',
              background: (writing || !text.trim() || isRecording || isTranscribing)
                ? '#475569'
                : 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
              color: '#fff',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {writing ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '18px',
                  height: '18px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                記録中...
              </>
            ) : (
              '記録する'
            )}
          </button>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px',
          color: '#64748b',
          fontSize: '12px'
        }}>
          Powered by Google Sheets
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }
        ::placeholder {
          color: #64748b;
        }
      `}</style>
    </main>
  );
}
